/**
 * Admin Audit Log viewer — filterable, paginated security event log
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { parseServerDate } from '../../utils/formatDateTime'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  AUDIT_CATEGORIES, getAuditEventTypes, listAuditLog, reviewAuditEntries, suggestAuditValues,
} from '../../services/admin'
import type {
  AuditCategory, AuditEventType, AuditLogFilters, AuditReviewFilter, AuditSortKey,
} from '../../services/admin'
import type { AuditLogEntry } from '../../types'
import { Pagination } from '../../components/Pagination'
import SuggestInput, { type Suggestion } from '../../components/SuggestInput'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { formatLocalDate } from '../../utils/formatDateTime'
import InfoHint from '../../components/InfoHint'
import { RowCheckbox, SelectAllCheckbox, BulkActionBar } from '../../components/BulkSelect'
import { useRowSelection } from '../../hooks/useRowSelection'
import PageHeading from '../../components/PageHeading'
import { AUDIT_LOG_HELP } from '../../content/pageHelp'
import { SortableHeader } from '../../components/SortableHeader'
import { useTableSort } from '../../hooks/useTableSort'
import {
  isCode, isPageNumber, listStateKey, oneOf, textUpTo, usePageResetOnChange, usePageSize,
  useSessionState,
} from '../../hooks/useSessionState'


/** Only an in-app path ("/licenses/…") becomes a link; anything else stays text. */
function isAppPath(link: string | null | undefined): link is string {
  return typeof link === 'string' && link.startsWith('/') && !link.startsWith('//')
}

// Time is what everyone wants newest-first, and it is the default the server applies when
// no `sort` is sent. The other three read as answers to "show me all the X together", so
// they open ascending. IP and Action are plain headers: the server has no sort for them.
const DESC_FIRST: readonly AuditSortKey[] = ['timestamp']
const SORT_KEYS: readonly AuditSortKey[] = ['timestamp', 'event_type', 'user_email', 'success']

const ALL_CATEGORIES: AuditCategory[] = AUDIT_CATEGORIES.map((c) => c.value)
const isCategoryList = (v: unknown) =>
  Array.isArray(v) && v.length > 0 && v.every(oneOf(ALL_CATEGORIES))

/** The IP / user boxes' suggestions, labelled with how often and when last seen. */
function suggester(field: 'ip_address' | 'user_email') {
  return async (q: string): Promise<Suggestion[]> =>
    (await suggestAuditValues(field, q)).map((s) => ({
      value: s.value,
      hint: `${s.count.toLocaleString()} entr${s.count === 1 ? 'y' : 'ies'} · last ${formatLocalDate(s.last_seen)}`,
    }))
}
const suggestIp = suggester('ip_address')
const suggestUser = suggester('user_email')

// Filters, page and sort are remembered for the tab (useSessionState), so following an
// entry's call sign or client link and coming Back finds the log as it was left.
const KEY = listStateKey('auditLog')
const isDate = (v: unknown) => typeof v === 'string' && /^(\d{4}-\d{2}-\d{2})?$/.test(v)

const EVENT_BADGE: Record<string, string> = {
  auth_login_success: 'bg-green-100 text-green-800',
  auth_login_failed: 'bg-red-100 text-red-800',
  auth_mfa_failed: 'bg-red-100 text-red-800',
  auth_account_locked: 'bg-red-200 text-red-900 font-semibold',
  auth_ip_blocked: 'bg-orange-100 text-orange-800',
  auth_device_untrusted: 'bg-orange-100 text-orange-800',
  auth_logout: 'bg-gray-100 text-gray-600',
  data_export: 'bg-blue-100 text-blue-800',
  admin_action: 'bg-purple-100 text-purple-800',
  admin_login_policy_updated: 'bg-purple-100 text-purple-800',
  user_management: 'bg-indigo-100 text-indigo-800',
}

function eventBadgeClass(eventType: string): string {
  if (EVENT_BADGE[eventType]) return EVENT_BADGE[eventType]
  if (eventType.startsWith('auth_')) return 'bg-blue-50 text-blue-700'
  if (eventType.startsWith('admin_')) return 'bg-purple-50 text-purple-700'
  if (eventType.startsWith('construction_')) return 'bg-yellow-50 text-yellow-700'
  return 'bg-gray-100 text-gray-600'
}

export default function AuditLogPage() {
  const { user } = useAuth()

  const [items, setItems] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Server-side: re-sorting has to reorder the whole filtered log, not the 50 rows on
  // screen. An audit review that sorted only the current page would look right and be
  // missing every entry on the other pages.
  const { sortKey, sortOrder, handleSort } = useTableSort<AuditSortKey>(
    'timestamp', 'desc',
    {
      descFirst: DESC_FIRST, onSortChange: () => setPage(1),
      persist: { key: KEY('sort'), keys: SORT_KEYS },
    },
  )

  // Filters
  const [userEmail, setUserEmail] = useSessionState(KEY('userEmail'), '', textUpTo(255))
  const [eventType, setEventType] = useSessionState(KEY('eventType'), '', isCode)
  const [ipAddress, setIpAddress] = useSessionState(KEY('ipAddress'), '', textUpTo(45))
  const [resource, setResource] = useSessionState(KEY('resource'), '', textUpTo(100))
  const [successFilter, setSuccessFilter] = useSessionState<'all' | 'success' | 'failure'>(
    KEY('success'), 'all', oneOf(['all', 'success', 'failure']),
  )
  const [dateFrom, setDateFrom] = useSessionState(KEY('dateFrom'), '', isDate)
  const [dateTo, setDateTo] = useSessionState(KEY('dateTo'), '', isDate)
  // Which kinds of activity to show; each can be hidden, "Sign-in & security" included.
  const [categories, setCategories] = useSessionState<AuditCategory[]>(
    KEY('categories'), ALL_CATEGORIES, isCategoryList,
  )
  // Flagged for review: what the Audit Log badge counts. Cuts across the categories.
  const [flaggedOnly, setFlaggedOnly] = useSessionState(KEY('flaggedOnly'), false)
  // With flagged on: the review queue (unreviewed), what has been reviewed, or both.
  const [reviewFilter, setReviewFilter] = useSessionState<AuditReviewFilter>(
    KEY('review'), 'unreviewed', oneOf(['unreviewed', 'reviewed', 'any']),
  )
  const [reviewNote, setReviewNote] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const [pageSize, setPageSize] = usePageSize('auditLog', 50)

  // Typed boxes settle before they reload the log; selects and tick-boxes act at once.
  const debouncedEmail = useDebouncedValue(userEmail)
  const debouncedIp = useDebouncedValue(ipAddress)
  const debouncedResource = useDebouncedValue(resource)

  const [eventTypes, setEventTypes] = useState<AuditEventType[]>([])
  useEffect(() => {
    getAuditEventTypes().then(setEventTypes).catch(() => setEventTypes([]))
  }, [])
  /** The Event select's choices, grouped under the categories that are showing. */
  const eventGroups = useMemo(
    () => AUDIT_CATEGORIES
      .map((c) => ({ ...c, types: eventTypes.filter((t) => t.category === c.value) }))
      .filter((g) => g.types.length > 0),
    [eventTypes],
  )

  function toggleCategory(value: AuditCategory, checked: boolean) {
    const next = ALL_CATEGORIES.filter((c) => (c === value ? checked : categories.includes(c)))
    if (next.length > 0) setCategories(next)  // the UI disables unticking the last one
  }

  // Detail expand
  const [expandedId, setExpandedId] = useState<number | null>(null)

  /** The filters on screen, in one place: the list request and "mark all matching
      reviewed" both use this, so they cannot disagree about what is showing. */
  const filters = useMemo<AuditLogFilters>(() => ({
    user_email: debouncedEmail.trim() || undefined,
    event_type: eventType || undefined,
    ip_address: debouncedIp.trim() || undefined,
    resource: debouncedResource.trim() || undefined,
    success: successFilter === 'success' ? true : successFilter === 'failure' ? false : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    significant_only: flaggedOnly || undefined,
    categories,
    review: flaggedOnly ? reviewFilter : 'any',
  }), [debouncedEmail, eventType, debouncedIp, debouncedResource, successFilter, dateFrom,
    dateTo, flaggedOnly, categories, reviewFilter])

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError('')
    try {
      const result = await listAuditLog({
        ...filters,
        page: p,
        page_size: pageSize,
        sort: sortKey,
        order: sortOrder,
      })
      setItems(result.items)
      setTotal(result.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log')
    } finally {
      setLoading(false)
    }
  }, [filters, pageSize, sortKey, sortOrder])

  useEffect(() => {
    load(page)
  }, [page, load])

  // Any filter, sort or page-size change goes back to page 1. Without this, choosing
  // a category while on page 4 asked for a page past the end: "85 entries" over an
  // empty table (Jesse, 2026-10-30).
  usePageResetOnChange(JSON.stringify([filters, pageSize, sortKey, sortOrder]), setPage)

  // Reviewing is for flagged entries that nobody has reviewed yet; only those rows can
  // be ticked.
  const selectable = flaggedOnly ? items.filter((i) => !i.reviewed_at).map((i) => i.id) : []
  const selection = useRowSelection<number>(
    selectable, JSON.stringify([filters, page, pageSize, sortKey, sortOrder]),
  )

  async function markReviewed() {
    setReviewing(true)
    setError('')
    try {
      await reviewAuditEntries(
        selection.allMatching ? { filters } : { ids: [...selection.selected] },
        reviewNote.trim(),
      )
      selection.clear()
      setReviewNote('')
      await load(page)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mark reviewed')
    } finally {
      setReviewing(false)
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    load(1)
  }

  function handleClear() {
    setUserEmail('')
    setEventType('')
    setIpAddress('')
    setResource('')
    setSuccessFilter('all')
    setDateFrom('')
    setDateTo('')
    setCategories(ALL_CATEGORIES)
    setFlaggedOnly(false)
    setReviewFilter('unreviewed')
    setPage(1)
  }

  if (!user?.is_superuser) return <Navigate to="/" replace />

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div>
      <PageHeading title="Audit Log" help={AUDIT_LOG_HELP} className="mb-6" />

      {/* Filters */}
      <div className="card mb-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="audit-user" className="label text-xs">User email</label>
              <SuggestInput
                id="audit-user"
                placeholder="Type to see who appears in the log"
                maxLength={255}
                value={userEmail}
                onChange={setUserEmail}
                fetchSuggestions={suggestUser}
              />
            </div>
            <div>
              <label htmlFor="audit-event" className="label text-xs">Event</label>
              <select
                id="audit-event"
                className="input text-sm"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                <option value="">All events</option>
                {eventGroups.map((g) => (
                  <optgroup key={g.value} label={g.label}>
                    {g.types.map((t) => (
                      <option key={t.event_type} value={t.event_type}>
                        {t.label} ({t.count.toLocaleString()})
                      </option>
                    ))}
                  </optgroup>
                ))}
                {/* A remembered type that has since left the log stays selectable. */}
                {eventType && !eventTypes.some((t) => t.event_type === eventType) && (
                  <option value={eventType}>{eventType}</option>
                )}
              </select>
            </div>
            <div>
              <label htmlFor="audit-ip" className="label text-xs">IP address</label>
              <SuggestInput
                id="audit-ip"
                placeholder="Type to see addresses in the log"
                maxLength={45}
                value={ipAddress}
                onChange={setIpAddress}
                fetchSuggestions={suggestIp}
              />
            </div>
            <div>
              <label htmlFor="audit-resource" className="label text-xs">Call sign or record</label>
              <input
                id="audit-resource"
                className="input text-sm"
                placeholder="e.g. WSKQ948"
                maxLength={100}
                value={resource}
                onChange={(e) => setResource(e.target.value)}
              />
            </div>
            <div>
              <label className="label text-xs">Result</label>
              <select className="input text-sm" value={successFilter} onChange={(e) => setSuccessFilter(e.target.value as typeof successFilter)}>
                <option value="all">All</option>
                <option value="success">Success only</option>
                <option value="failure">Failures only</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">From date</label>
              <input
                type="date"
                className="input text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="label text-xs">To date</label>
              <input
                type="date"
                className="input text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <fieldset className="toolbar gap-x-4 gap-y-2">
            <legend className="sr-only">Kinds of activity to show</legend>
            <span className="text-sm text-gray-600">Show</span>
            {AUDIT_CATEGORIES.map((c) => {
              const checked = categories.includes(c.value)
              const last = checked && categories.length === 1
              return (
                <label key={c.value} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={checked}
                    disabled={last}
                    title={last ? 'Pick another kind before turning this one off.' : undefined}
                    onChange={(e) => toggleCategory(c.value, e.target.checked)}
                  />
                  {c.label}
                </label>
              )
            })}
          </fieldset>
          <div className="toolbar gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={flaggedOnly}
                onChange={(e) => setFlaggedOnly(e.target.checked)}
              />
              Flagged for review only
            </label>
            {flaggedOnly && (
              <select
                className="filter-control"
                aria-label="Reviewed or not"
                value={reviewFilter}
                onChange={(e) => setReviewFilter(e.target.value as AuditReviewFilter)}
              >
                <option value="unreviewed">Not yet reviewed</option>
                <option value="reviewed">Already reviewed</option>
                <option value="any">Reviewed or not</option>
              </select>
            )}
            <InfoHint label="What is flagged for review?">
              Entries worth a second look, whatever their kind: failed sign-ins, lockouts and
              blocked addresses; changes to passwords, passkeys, two-step sign-in and its
              exemptions; users and administrators added or changed; login policies and the
              mail server changed; anyone taken off Do Not Contact or contacted despite it;
              backups and data exports; clients or logged calls deleted; notification
              addresses changed. Routine sign-ins and page views are not flagged. Tick entries
              and press Mark reviewed once you have looked; the menu badge counts what is
              left, and every review is itself recorded.
            </InfoHint>
            <div className="ml-auto flex gap-2">
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>
                Clear
              </button>
              <button type="submit" className="btn btn-primary btn-sm">
                Search
              </button>
            </div>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      <BulkActionBar
        count={selection.count}
        total={total}
        pageSize={pageSize}
        allMatching={selection.allMatching}
        onSelectAllMatching={selection.selectAllMatching}
        onClear={selection.clear}
      >
        <input
          className="input py-1 text-sm w-64"
          aria-label="Review note (optional)"
          placeholder="Note (optional), e.g. all expected"
          maxLength={500}
          value={reviewNote}
          onChange={(e) => setReviewNote(e.target.value)}
        />
        <button type="button" className="btn-sm btn-primary disabled:opacity-50"
                disabled={reviewing} onClick={markReviewed}>
          {reviewing ? 'Saving…' : 'Mark reviewed'}
        </button>
      </BulkActionBar>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Results</h2>
          <span className="text-sm text-gray-500">{total.toLocaleString()} entries</span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No entries match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table text-xs">
              <thead>
                <tr>
                  {flaggedOnly && (
                    <th className="w-px">
                      <SelectAllCheckbox state={selection.pageState} onToggle={selection.togglePage} />
                    </th>
                  )}
                  <SortableHeader sortKey="timestamp" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="w-36">
                    Time
                  </SortableHeader>
                  <SortableHeader sortKey="event_type" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort}>
                    Event
                  </SortableHeader>
                  <SortableHeader sortKey="user_email" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="hidden md:table-cell">
                    User
                  </SortableHeader>
                  <th className="hidden lg:table-cell">IP</th>
                  <th className="hidden sm:table-cell">Action</th>
                  <SortableHeader sortKey="success" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="w-16">
                    Result
                  </SortableHeader>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.map((entry) => (
                  <>
                    <tr
                      key={entry.id}
                      className={`hover:bg-gray-50 cursor-pointer ${!entry.success ? 'bg-red-50' : ''}`}
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    >
                      {flaggedOnly && (
                        <td className="w-px" onClick={(e) => e.stopPropagation()}>
                          {entry.reviewed_at ? (
                            <span className="text-green-700" title={`Reviewed by ${entry.reviewed_by_name ?? 'an admin'}`}>✓</span>
                          ) : (
                            <RowCheckbox
                              checked={selection.isSelected(entry.id)}
                              onChange={() => selection.toggle(entry.id)}
                              label={`${entry.event_type} at ${entry.timestamp}`}
                            />
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap text-gray-500">
                        {format(parseServerDate(entry.timestamp)!, 'MM/dd HH:mm:ss')}
                      </td>
                      <td>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-mono ${eventBadgeClass(entry.event_type)}`}>
                          {entry.event_type}
                        </span>
                      </td>
                      <td className="hidden md:table-cell text-gray-700 max-w-[180px] truncate">
                        {entry.user_email || <span className="text-gray-400 italic">—</span>}
                      </td>
                      <td className="hidden lg:table-cell text-gray-500 font-mono">
                        {entry.ip_address || '—'}
                      </td>
                      <td className="hidden sm:table-cell text-gray-500">
                        {entry.action || '—'}
                      </td>
                      <td>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${entry.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {entry.success ? 'OK' : 'FAIL'}
                        </span>
                      </td>
                      <td className="text-gray-400">
                        {expandedId === entry.id ? '▲' : '▼'}
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr key={`detail-${entry.id}`} className="bg-gray-50">
                        <td colSpan={flaggedOnly ? 8 : 7} className="px-4 py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-gray-700">
                            <div><span className="font-medium">ID:</span> {entry.id}</div>
                            <div><span className="font-medium">User ID:</span> {entry.user_id ?? '—'}</div>
                            <div><span className="font-medium">Email:</span> {entry.user_email ?? '—'}</div>
                            <div><span className="font-medium">IP:</span> {entry.ip_address ?? '—'}</div>
                            <div>
                              <span className="font-medium">Resource:</span>{' '}
                              {entry.resource_label ? (
                                <>
                                  {entry.resource_type}{' '}
                                  {isAppPath(entry.resource_link) ? (
                                    <Link to={entry.resource_link} className="text-primary-600 hover:underline">
                                      {entry.resource_label}
                                    </Link>
                                  ) : entry.resource_label}
                                  {entry.resource_id !== entry.resource_label && (
                                    <span className="text-gray-500"> (#{entry.resource_id})</span>
                                  )}
                                </>
                              ) : (
                                <>{entry.resource_type ?? '—'} {entry.resource_id ? `#${entry.resource_id}` : ''}</>
                              )}
                            </div>
                            <div><span className="font-medium">Action:</span> {entry.action ?? '—'}</div>
                            {entry.reviewed_at && (
                              <div className="col-span-3 text-green-800">
                                <span className="font-medium">Reviewed</span>{' '}
                                {format(parseServerDate(entry.reviewed_at)!, 'MM/dd/yyyy HH:mm')}
                                {entry.reviewed_by_name && <> by {entry.reviewed_by_name}</>}
                                {entry.review_note && <>: “{entry.review_note}”</>}
                              </div>
                            )}
                            {entry.error_message && (
                              <div className="col-span-3 text-red-600">
                                <span className="font-medium">Error:</span> {entry.error_message}
                              </div>
                            )}
                            {entry.details && Object.keys(entry.details).length > 0 && (
                              <div className="col-span-3">
                                <span className="font-medium">Details:</span>
                                <pre className="mt-1 bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                                  {JSON.stringify(entry.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page} totalPages={totalPages} onPageChange={setPage}
          total={total} pageSize={pageSize} onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  )
}
